// src/utils/trialOffer.js
//
// "Is it time to offer this person an extension, and have they already had one?"
//
// ===========================================================================
// TWO TRIAL CLOCKS — READ THIS FIRST
// ===========================================================================
// This app has two unrelated trial systems and they will disagree unless you
// keep them apart deliberately:
//
//   LOCAL trial   users/{uid}.trial.endDate
//                 Written at registration (Register.js, TRIAL_DAYS). No card,
//                 no Stripe object. hooks/useAccessLevel.hasActiveTrial()
//                 reads it and returns ACCESS.PAID, which is why a trial user
//                 gets the full toolset.
//
//   STRIPE trial  subscription.status === "trialing"
//                 Created by subscription_data.trial_period_days at checkout.
//                 A card IS on file. Stripe owns the clock and will bill.
//
// The rule this file enforces: **the moment someone reaches checkout, Stripe
// takes over.** The local trial is marked "converted" and stops being
// consulted. Without that, a person who accepts a 2-month extension AND then
// buys gets both clocks running, and your dashboard shows a different date
// than Stripe's invoice.
//
// ===========================================================================
// EXTENSION POLICY
// ===========================================================================
// Extensions are granted by a Cloud Function, never by the client. The client
// can write to its own users/{uid} document, so a client-side extension is a
// devtools edit away from being infinite.
//
// One extension per account, tracked by trial.extensions[]. The window opens
// 5 days out and the offer keeps showing until it's taken or the trial ends —
// a teacher who sees it on a Friday and means to deal with it Monday should
// still find it there.

export const OFFER_WINDOW_DAYS = 5;

/** Firestore Timestamp | Date | ISO string -> Date | null */
export function toDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function daysUntil(value) {
  const end = toDate(value);
  if (!end) return null;
  return Math.ceil((end.getTime() - Date.now()) / 86400000);
}

/**
 * Everything the dashboard needs to decide what to show.
 *
 * @param {object} profile   users/{uid} document data
 * @param {object} opts
 * @param {boolean} opts.hasPaidSubscription  from useAccessLevel
 */
export function evaluateTrial(profile, { hasPaidSubscription = false } = {}) {
  const trial = profile?.trial || {};
  const end = toDate(trial.endDate);
  const remaining = daysUntil(trial.endDate);

  const extensions = Array.isArray(trial.extensions) ? trial.extensions : [];
  const alreadyExtended = extensions.length > 0;

  // Stripe owns the clock from checkout onwards.
  const converted = trial.status === "converted" || hasPaidSubscription;

  const active = !converted && trial.status === "active" && remaining !== null && remaining > 0;
  const expired =
    !converted &&
    (trial.status === "expired" || (remaining !== null && remaining <= 0));

  // Dismissals are remembered so the modal doesn't reappear on every page
  // load, but they lapse after a day — this is the last thing standing
  // between the account and a hard lock, so once per day is fair.
  const dismissedAt = toDate(trial.offerDismissedAt);
  const dismissedRecently =
    dismissedAt && Date.now() - dismissedAt.getTime() < 86400000;

  const shouldOfferExtension =
    active &&
    !alreadyExtended &&
    !dismissedRecently &&
    remaining <= OFFER_WINDOW_DAYS;

  // Someone who already took the extension still gets the discount pitch —
  // they just don't get more free time.
  const shouldOfferDiscountOnly =
    active && alreadyExtended && !dismissedRecently && remaining <= OFFER_WINDOW_DAYS;

  return {
    active,
    expired,
    converted,
    endDate: end,
    daysRemaining: remaining,
    alreadyExtended,
    shouldOfferExtension,
    shouldOfferDiscountOnly,
    shouldOffer: shouldOfferExtension || shouldOfferDiscountOnly,
  };
}

/**
 * What an expired trial still gets to see.
 *
 * Deliberately NOT a hard lock. A teacher who spent three weeks building a
 * roster and watching their students' scores will convert far better looking
 * at that data behind a "Reactivate" button than at a screen that pretends
 * their classroom never existed. See hooks/useAccessLevel.js — ACCESS.FREE
 * keeps `viewContent` with a topic limit; this adds read-only classroom on
 * top for accounts that had a trial.
 */
export function expiredTrialCapabilities(profile) {
  const hadTrial = Boolean(profile?.trial?.endDate);
  return {
    classroomReadOnly: hadTrial, // see roster + history, can't add students
    canAddStudents: false,
    canAssignWork: false,
  };
}

export default evaluateTrial;
